import type { JSONContent } from "@tiptap/react";
import { Fragment, type ReactNode } from "react";

import { focusRing } from "../../foundations/a11y";
import { cn } from "../../lib/cn";

export function isSafeRichTextLink(value: string): boolean {
  try {
    return ["https:", "http:", "mailto:"].includes(new URL(value).protocol);
  } catch {
    return false;
  }
}

export const richTextClasses =
  "text-body text-text break-words [&_p]:my-3 [&_h2]:mb-3 [&_h2]:mt-6 [&_h2]:text-heading [&_h3]:mb-2 [&_h3]:mt-5 [&_h3]:font-semibold [&_ul]:my-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:my-3 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:my-1 [&_blockquote]:border-l-2 [&_blockquote]:border-separator [&_blockquote]:pl-4";

function renderNode(node: JSONContent, depth = 0): ReactNode {
  if (depth > 12) return null;
  const children = node.content?.map((child, index) => (
    <Fragment key={index}>{renderNode(child, depth + 1)}</Fragment>
  ));
  switch (node.type) {
    case "doc":
      return children;
    case "paragraph":
      return <p>{children ?? <br />}</p>;
    case "heading":
      return node.attrs?.level === 3 ? (
        <h3>{children}</h3>
      ) : (
        <h2>{children}</h2>
      );
    case "bulletList":
      return <ul>{children}</ul>;
    case "orderedList":
      return <ol start={Number(node.attrs?.start) || 1}>{children}</ol>;
    case "listItem":
      return <li>{children}</li>;
    case "blockquote":
      return <blockquote>{children}</blockquote>;
    case "hardBreak":
      return <br />;
    case "text": {
      let text: ReactNode = node.text;
      for (const mark of node.marks ?? []) {
        if (mark.type === "bold") text = <strong>{text}</strong>;
        if (mark.type === "italic") text = <em>{text}</em>;
        if (
          mark.type === "link" &&
          typeof mark.attrs?.href === "string" &&
          isSafeRichTextLink(mark.attrs.href)
        )
          text = (
            <a
              href={mark.attrs.href}
              className={cn(
                "text-accent underline underline-offset-2",
                focusRing,
              )}
            >
              {text}
            </a>
          );
      }
      return text;
    }
    default:
      return null;
  }
}

/** Render structured text as React elements; raw HTML is never inserted. */
export function RichTextContent({
  value,
  className,
}: {
  value: string;
  className?: string;
}) {
  try {
    const content = JSON.parse(value) as JSONContent;
    return (
      <div className={cn(richTextClasses, className)}>
        {renderNode(content)}
      </div>
    );
  } catch {
    return <p role="alert">This content could not be displayed.</p>;
  }
}
